import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

interface AssignmentFilters {
  courseType: string;
  courseId: string;
  intakeId: string;
  termNumber: string;
  groupNumber: string;
  search: string;
  isFormative: boolean;
  isSummative: boolean;
  isActive: boolean;
  isCompleted: boolean;
}

interface FilterState {
  filters: AssignmentFilters;
  currentPage: number;
}

const getEmptyFilters = (): AssignmentFilters => ({
  courseType: '',
  courseId: '',
  intakeId: '',
  termNumber: '',
  groupNumber: '',
  search: '',
  isFormative: false,
  isSummative: false,
  isActive: false,
  isCompleted: false,
});

const getFiltersFromUrl = (searchParams: URLSearchParams): AssignmentFilters => {
  // If URL has no params, return empty filters
  if (searchParams.toString() === '') {
    return getEmptyFilters();
  }

  return {
    courseType: searchParams.get('courseType') || '',
    courseId: searchParams.get('courseId') || '',
    intakeId: searchParams.get('intakeId') || '',
    termNumber: searchParams.get('termNumber') || '',
    groupNumber: searchParams.get('groupNumber') || '',
    search: searchParams.get('search') || '',
    isFormative: searchParams.get('isFormative') === 'true',
    isSummative: searchParams.get('isSummative') === 'true',
    isActive: searchParams.get('isActive') === 'true',
    isCompleted: searchParams.get('isCompleted') === 'true',
  };
};

const buildUrlParams = (filters: AssignmentFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.courseType) params.set('courseType', filters.courseType);
  if (filters.courseId) params.set('courseId', filters.courseId);
  if (filters.intakeId) params.set('intakeId', filters.intakeId);
  if (filters.termNumber) params.set('termNumber', filters.termNumber);
  if (filters.groupNumber) params.set('groupNumber', filters.groupNumber);
  if (filters.search) params.set('search', filters.search);
  if (filters.isFormative) params.set('isFormative', 'true');
  if (filters.isSummative) params.set('isSummative', 'true');
  if (filters.isActive) params.set('isActive', 'true');
  if (filters.isCompleted) params.set('isCompleted', 'true');
  return params;
};

export const useTeacherAssignmentFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInternalUpdate = useRef(false);

  const [state, setState] = useState<FilterState>(() => ({
    filters: getFiltersFromUrl(searchParams),
    currentPage: 1,
  }));

  // Sync state to URL
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const newParams = buildUrlParams(state.filters);
    isInternalUpdate.current = true;
    setSearchParams(newParams, { replace: true });
  }, [state.filters, setSearchParams]);

  // Sync URL to state (browser back/forward)
  useEffect(() => {
    if (isInternalUpdate.current) return;

    const urlFilters = getFiltersFromUrl(searchParams);
    setState((prev) => {
      if (JSON.stringify(prev.filters) !== JSON.stringify(urlFilters)) {
        return {
          filters: urlFilters,
          currentPage: 1, // Сбрасываем страницу при изменении URL
        };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateFilter = <K extends keyof AssignmentFilters>(key: K, value: AssignmentFilters[K]) => {
    setState((prev) => ({
      filters: { ...prev.filters, [key]: value },
      currentPage: 1, // Синхронно сбрасываем страницу при изменении фильтра
    }));
  };

  const resetDependentFilters = (resetFrom: keyof AssignmentFilters) => {
    setState((prev) => {
      const resetMap: Record<string, Partial<AssignmentFilters>> = {
        courseType: { courseId: '', intakeId: '', termNumber: '', groupNumber: '' },
        courseId: { intakeId: '', termNumber: '', groupNumber: '' },
        intakeId: { termNumber: '', groupNumber: '' },
      };
      return {
        filters: { ...prev.filters, ...resetMap[resetFrom] },
        currentPage: 1, // Сбрасываем страницу при сбросе зависимых фильтров
      };
    });
  };

  const setCurrentPage = (page: number) => {
    setState((prev) => ({ ...prev, currentPage: page }));
  };

  return {
    filters: state.filters,
    currentPage: state.currentPage,
    setCurrentPage,
    updateFilter,
    resetDependentFilters,
  };
};
